<template>
  <header
    class="bg-white/80 dark:bg-dark-bg/80 border-b border-slate-200 dark:border-white/5 px-4 md:px-8 py-4 flex justify-between items-center sticky top-0 z-40 backdrop-blur-xl transition-colors duration-300"
  >
    <div class="flex items-center gap-3 md:gap-4">
      <button
        @click="$emit('toggle-sidebar')"
        class="lg:hidden p-2 rounded-xl bg-slate-100 dark:bg-dark-input text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-primary transition-all border border-slate-200 dark:border-white/5"
      >
        <i class="fas fa-bars text-base"></i>
      </button>

      <h1
        class="text-sm md:text-lg font-black text-slate-800 dark:text-white tracking-widest uppercase italic truncate max-w-xs md:max-w-none"
      >
        {{ currentRouteName }}
      </h1>
    </div>

    <div class="flex items-center gap-2 md:gap-6 relative">
      <div
        class="hidden sm:flex items-center gap-1 bg-slate-100 dark:bg-dark-input p-1 rounded-xl border border-slate-200 dark:border-white/5"
      >
        <button
          @click="changeLocale('en')"
          :class="
            locale === 'en'
              ? 'bg-white dark:bg-dark-surface shadow-sm text-primary'
              : 'text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-primary'
          "
          class="px-2 py-1 rounded-lg text-xs font-black uppercase tracking-widest transition-all"
        >
          EN
        </button>
        <button
          @click="changeLocale('ar')"
          :class="
            locale === 'ar'
              ? 'bg-white dark:bg-dark-surface shadow-sm text-primary'
              : 'text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-primary'
          "
          class="px-2 py-1 rounded-lg text-xs font-black uppercase tracking-widest transition-all"
        >
          AR
        </button>
      </div>

      <button
        @click="toggleTheme"
        class="p-2.5 rounded-xl bg-slate-100 dark:bg-dark-input text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-primary transition-all border border-slate-200 dark:border-white/5"
        :title="$t('toggle_theme')"
      >
        <i v-if="isDark" class="fas fa-sun"></i>
        <i v-else class="fas fa-moon"></i>
      </button>

      <button
        class="hidden sm:block text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-white transition-colors"
      >
        <i class="fas fa-search h-5 w-5"></i>
      </button>

      <div class="relative">
        <button
          @click.stop="toggleNotificationDropdown"
          class="relative p-2.5 rounded-xl bg-slate-100 dark:bg-dark-input border border-slate-200 dark:border-white/5 hover:bg-slate-200 dark:hover:bg-dark-hover transition-all"
          :aria-label="notificationTitle"
        >
          <i class="fas fa-bell text-slate-500 dark:text-slate-400"></i>
          <span
            v-if="store.unreadCount"
            class="absolute -top-1 -right-1 w-5 h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg border-2 border-white dark:border-dark-bg"
          >
            {{ unreadBadge }}
          </span>
        </button>

        <transition name="dropdown">
          <div
            v-if="notificationDropdown"
            class="absolute right-0 mt-3 w-80 sm:w-96 bg-white dark:bg-dark-surface rounded-2xl shadow-2xl border border-slate-200 dark:border-white/5 z-50 overflow-visible"
          >
            <div
              class="px-5 py-4 border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-dark-input"
            >
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="text-sm font-black text-slate-900 dark:text-white">
                    {{ notificationTitle }}
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {{ notificationSubtitle }}
                  </p>
                </div>

                <div
                  v-if="store.unreadCount"
                  class="flex-shrink-0 px-2 py-1 rounded-full bg-blue-100 dark:bg-dark-hover text-xs font-black text-blue-700 dark:text-primary"
                >
                  {{ unreadCountText }}
                </div>
              </div>

              <div class="flex items-center gap-2 mt-4">
                <button
                  @click.stop="refreshNotifications"
                  :disabled="store.loading || actionLoading"
                  class="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-black bg-slate-100 dark:bg-dark-input text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-dark-hover transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i
                    class="fas fa-rotate-right"
                    :class="{ 'animate-spin': store.loading }"
                  ></i>
                  {{ refreshLabel }}
                </button>

                <button
                  @click.stop="markAllRead"
                  :disabled="
                    !store.unreadCount || actionLoading || store.loading
                  "
                  class="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-black bg-primary text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i
                    class="fas"
                    :class="
                      actionLoading
                        ? 'fa-spinner animate-spin'
                        : 'fa-check-double'
                    "
                  ></i>
                  {{ markAllLabel }}
                </button>
              </div>
            </div>

            <div
              class="max-h-96 overflow-y-auto"
              @scroll.passive="handleNotificationScroll"
            >
              <div
                v-if="store.loading && !store.notifications.length"
                class="p-5 space-y-3"
              >
                <div
                  v-for="item in 3"
                  :key="item"
                  class="rounded-2xl border border-slate-200 dark:border-white/5 p-4 animate-pulse"
                >
                  <div
                    class="h-3 rounded bg-slate-200 dark:bg-white/10 w-2/3"
                  ></div>
                  <div
                    class="h-3 rounded bg-slate-100 dark:bg-white/5 mt-3"
                  ></div>
                  <div
                    class="h-3 rounded bg-slate-100 dark:bg-white/5 mt-2 w-1/2"
                  ></div>
                </div>
              </div>

              <div
                v-else-if="!store.notifications.length"
                class="px-6 py-10 text-center"
              >
                <div
                  class="w-14 h-14 mx-auto rounded-2xl bg-slate-100 dark:bg-dark-input flex items-center justify-center text-slate-400 dark:text-slate-500"
                >
                  <i class="fas fa-bell-slash text-lg"></i>
                </div>
                <p
                  class="text-sm font-black text-slate-700 dark:text-white mt-4"
                >
                  {{ emptyTitle }}
                </p>
                <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {{ emptySubtitle }}
                </p>
              </div>

              <div v-else class="p-3 space-y-2">
                <button
                  v-for="notification in store.notifications"
                  :key="notification.id"
                  @click="markRead(notification.id)"
                  class="w-full text-left rounded-2xl border p-4 transition-all group"
                  :class="
                    notification.is_read
                      ? 'bg-white dark:bg-dark-surface border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10'
                      : 'bg-blue-50 dark:bg-dark-hover border-blue-100 dark:border-dark-hover hover:border-blue-200 dark:hover:border-primary shadow-sm'
                  "
                >
                  <div class="flex items-start gap-3">
                    <div
                      class="mt-1 w-3 h-3 rounded-full flex-shrink-0"
                      :class="
                        notification.is_read
                          ? 'bg-slate-300 dark:bg-slate-600'
                          : 'bg-primary ring-4 ring-blue-100'
                      "
                    ></div>

                    <div class="min-w-0 flex-1">
                      <div class="flex items-start justify-between gap-3">
                        <p
                          class="text-sm font-black truncate"
                          :class="
                            notification.is_read
                              ? 'text-slate-700 dark:text-slate-200'
                              : 'text-slate-900 dark:text-white'
                          "
                        >
                          {{ notification.title }}
                        </p>

                        <span
                          v-if="!notification.is_read"
                          class="flex-shrink-0 px-2 py-1 rounded-full bg-blue-100 dark:bg-dark-input text-xs font-black text-blue-700 dark:text-primary"
                        >
                          {{ newLabel }}
                        </span>
                      </div>

                      <p
                        class="text-xs mt-1.5 leading-5 break-words"
                        :class="
                          notification.is_read
                            ? 'text-slate-500 dark:text-slate-400'
                            : 'text-slate-600 dark:text-slate-300'
                        "
                      >
                        {{ notification.body }}
                      </p>

                      <div
                        class="mt-3 flex items-center justify-between gap-3 text-xs"
                      >
                        <span class="text-slate-400 dark:text-slate-500">
                          {{ formatNotificationDate(notification.created_at) }}
                        </span>
                        <span
                          class="font-bold transition-colors"
                          :class="
                            notification.is_read
                              ? 'text-slate-400 dark:text-slate-500'
                              : 'text-primary'
                          "
                        >
                          {{
                            notification.is_read ? readLabel : tapToReadLabel
                          }}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>

                <div
                  v-if="store.loadingMore"
                  class="py-4 flex items-center justify-center gap-3 text-xs font-bold text-slate-500 dark:text-slate-400"
                >
                  <span
                    class="w-5 h-5 rounded-full border-2 border-slate-200 dark:border-slate-600 border-t-primary animate-spin"
                  ></span>
                  <span>{{ loadMoreLabel }}</span>
                </div>

                <div
                  v-else-if="!store.hasMore && store.notifications.length"
                  class="py-2 text-center text-xs text-slate-400 dark:text-slate-500"
                >
                  {{ endOfListLabel }}
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <button
        @click.stop="toggleDropdown"
        class="flex items-center gap-3 p-1.5 pr-4 rounded-2xl bg-slate-100 dark:bg-dark-input border border-slate-200 dark:border-white/5 hover:border-blue-600/20 dark:hover:border-white/10 transition-all focus:outline-none group shadow-sm dark:shadow-lg dark:shadow-black/20"
      >
        <div
          class="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-black shadow-inner"
        >
          {{ username.charAt(0).toUpperCase() }}
        </div>
        <div class="text-left hidden md:block">
          <p
            class="text-xs font-black text-slate-800 dark:text-white leading-none mb-1"
          >
            {{ username }}
          </p>
          <p
            class="text-xs text-slate-500 font-bold uppercase tracking-tighter"
          >
            {{ emailAlias }}
          </p>
        </div>
        <i
          class="fas fa-chevron-down w-3.5 h-3.5 text-slate-400 dark:text-slate-500 transition-transform duration-300 group-hover:text-blue-600 dark:group-hover:text-white"
          :class="{ 'rotate-180': open }"
        ></i>
      </button>

      <transition name="dropdown">
        <div
          v-if="open"
          class="absolute right-0 top-16 w-56 bg-white dark:bg-dark-surface rounded-2xl shadow-2xl border border-slate-200 dark:border-white/5 z-50 overflow-hidden backdrop-blur-2xl"
        >
          <div
            class="px-5 py-4 border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-white/5"
          >
            <p class="text-sm font-black text-slate-800 dark:text-white">
              {{ username }}
            </p>
            <p
              class="text-xs text-slate-500 font-bold truncate mt-0.5 uppercase tracking-widest"
            >
              {{ email }}
            </p>
          </div>

          <div class="p-2">
            <button
              class="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all"
            >
              <i class="fas fa-user-cog"></i> {{ $t("profile_settings") }}
            </button>
            <button
              @click="logoutUser"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-red-500 dark:text-red-400 hover:text-white hover:bg-red-500 rounded-xl transition-all mt-1"
            >
              <i class="fas fa-sign-out-alt"></i> {{ $t("sign_out") }}
            </button>
          </div>
        </div>
      </transition>
    </div>
  </header>
</template>

<script>
import authService from "@/services/auth.service";
import { isDark, toggleTheme } from "@/utils/theme";
import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";
import { useNotificationStore } from "@/stores/notification";

export default {
  name: "Navbar",
  emits: ["toggle-sidebar"],
  data() {
    return {
      username: "User",
      email: "",
      open: false,
    };
  },
  setup() {
    const { locale } = useI18n();
    const store = useNotificationStore();
    const notificationDropdown = ref(false);
    const actionLoading = ref(false);

    const changeLocale = (lang) => {
      locale.value = lang;
    };

    const isArabic = computed(() => locale.value === "ar");
    const notificationTitle = computed(() =>
      isArabic.value ? "الإشعارات" : "Notifications"
    );
    const notificationSubtitle = computed(() =>
      store.unreadCount
        ? isArabic.value
          ? "تابع آخر التحديثات والتنبيهات الجديدة"
          : "Stay updated with the latest alerts"
        : isArabic.value
        ? "لا توجد إشعارات غير مقروءة حاليًا"
        : "You are all caught up"
    );
    const refreshLabel = computed(() => (isArabic.value ? "تحديث" : "Refresh"));
    const markAllLabel = computed(() =>
      isArabic.value ? "قراءة الكل" : "Mark all as read"
    );
    const emptyTitle = computed(() =>
      isArabic.value ? "لا توجد إشعارات" : "No notifications yet"
    );
    const emptySubtitle = computed(() =>
      isArabic.value
        ? "أي إشعار جديد سيظهر هنا مباشرة."
        : "New alerts will appear here."
    );
    const newLabel = computed(() => (isArabic.value ? "جديد" : "New"));
    const readLabel = computed(() => (isArabic.value ? "تمت القراءة" : "Read"));
    const tapToReadLabel = computed(() =>
      isArabic.value ? "اضغط لتعليمها كمقروءة" : "Tap to mark as read"
    );
    const unreadCountText = computed(() =>
      isArabic.value
        ? `${store.unreadCount} غير مقروء`
        : `${store.unreadCount} unread`
    );
    const unreadBadge = computed(() =>
      store.unreadCount > 99 ? "99+" : store.unreadCount
    );
    const loadMoreLabel = computed(() =>
      isArabic.value ? "جاري تحميل المزيد..." : "Loading more..."
    );
    const endOfListLabel = computed(() =>
      isArabic.value ? "تم عرض كل الإشعارات" : "All notifications loaded"
    );

    const toggleNotificationDropdown = () => {
      notificationDropdown.value = !notificationDropdown.value;
    };

    const refreshNotifications = async () => {
      try {
        await store.fetchNotifications();
      } catch (error) {
        console.error("Refresh notifications failed:", error);
      }
    };

    const markRead = async (id) => {
      try {
        await store.markAsRead(id);
      } catch (error) {
        console.error("Mark notification as read failed:", error);
      }
    };

    const markAllRead = async () => {
      if (!store.unreadCount || actionLoading.value) {
        return;
      }

      actionLoading.value = true;

      try {
        await store.markAllAsRead();
      } catch (error) {
        console.error("Mark all notifications as read failed:", error);
      } finally {
        actionLoading.value = false;
      }
    };

    const handleNotificationScroll = async (event) => {
      const element = event.target;

      if (!element) {
        return;
      }

      const remainingScroll =
        element.scrollHeight - element.scrollTop - element.clientHeight;

      if (remainingScroll > 40) {
        return;
      }

      try {
        await store.loadMoreNotifications();
      } catch (error) {
        console.error("Load more notifications failed:", error);
      }
    };

    const formatNotificationDate = (value) => {
      if (!value) {
        return "";
      }

      return new Date(value).toLocaleString(
        isArabic.value ? "ar-EG" : "en-US",
        {
          dateStyle: "medium",
          timeStyle: "short",
        }
      );
    };

    return {
      actionLoading,
      changeLocale,
      emptySubtitle,
      emptyTitle,
      formatNotificationDate,
      handleNotificationScroll,
      isArabic,
      isDark,
      endOfListLabel,
      loadMoreLabel,
      locale,
      markAllLabel,
      markAllRead,
      markRead,
      newLabel,
      notificationDropdown,
      notificationSubtitle,
      notificationTitle,
      readLabel,
      refreshLabel,
      refreshNotifications,
      store,
      tapToReadLabel,
      toggleNotificationDropdown,
      toggleTheme,
      unreadBadge,
      unreadCountText,
    };
  },
  computed: {
    currentRouteName() {
      return this.$route.name || "Overview";
    },
    emailAlias() {
      if (!this.email) {
        return "user";
      }

      return this.email.split("@")[0];
    },
  },
  methods: {
    toggleDropdown() {
      this.open = !this.open;
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.open = false;
        this.notificationDropdown = false;
      }
    },
    async logoutUser() {
      this.open = false;
      this.notificationDropdown = false;
      await authService.logout();
      this.$router.push("/login");
    },
  },
  mounted() {
    const user = authService.getUser();

    if (user) {
      this.username = user.username || user.name || user.email || "User";
      this.email = user.email || "";
    }

    document.addEventListener("click", this.handleClickOutside);
  },
  unmounted() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}
</style>
